// (c) 2023 Acellera Ltd http://www.acellera.com
// All Rights Reserved
// No redistribution in whole or part
//
type ApiCallResult =
  | { result: Response; success: true; error?: undefined }
  | { success: false; error: string; result?: undefined; logout?: boolean };

async function apicallResult(
  apiCallFactory: () => Promise<Response>
): Promise<ApiCallResult> {
  const result = await apiCallFactory();
  if (!result.ok) {
    const resultJson = await result.json();
    return { success: false, error: `${resultJson.message}` };
  }
  return { success: true, result: result };
}

async function refreshAccessToken(url: string): Promise<{
  success: boolean;
  result?: Response;
  error?: string;
}> {
  return await apicallResult(() =>
    fetch(`${url}/v2/auth/refresh-token`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
  );
}

export async function executeWithTokenRefreshWorker(
  apiCallFactory: () => Promise<Response>,
  url: string
): Promise<ApiCallResult> {
  // If call is successful, return result. Otherwise check if it's because of the access token
  const {
    success: call1Success,
    error: call1Error,
    result: call1Result,
  } = await apicallResult(apiCallFactory);
  if (call1Success) return { success: true, result: call1Result };
  if (!call1Error.startsWith("Authentication failed."))
    return { success: false, error: call1Error };
  // Call failed because of the access token. Refresh the token and try again
  const { success: refreshSuccess, error: refreshError } =
    await refreshAccessToken(url);
  let errorMessage;
  let logout = false;
  if (refreshSuccess) {
    const {
      success: call2Success,
      error: call2Error,
      result: call2Result,
    } = await apicallResult(apiCallFactory);
    if (call2Success) return { success: true, result: call2Result };
    else {
      errorMessage = call2Error;
      if (call2Error.startsWith("Authentication failed.")) {
        logout = true;
      }
    }
  } else {
    errorMessage = refreshError;
    logout = true;
  }
  return { success: false, error: errorMessage as string, logout: logout };
}
